import { useState } from 'react'
import { AlertCircle, UserMinus, Undo2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { useReleaseDriverBench } from '../../hooks/backend/fleet'
import { type BenchDriver, formatFullDate } from '../../utils/allocation'

// Who has been deliberately held off the roster, why, by whom and since when.
//
// This panel exists because a bench costs a man shifts and therefore pay. A
// decision with that consequence must be visible on the screen where it was
// made, not only in an audit table further down the page, and it must show the
// reason next to the name so nobody has to ask which of two managers benched
// him or why. Ending a bench is as deliberate as starting one and needs its own
// reason.
function ReleaseBenchDialog({
  driver,
  onReleased,
}: {
  driver: BenchDriver
  onReleased: () => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const release = useReleaseDriverBench()

  const reasonValid = reason.trim().length > 0
  const canSubmit = reasonValid && !release.loading

  async function handleRelease() {
    if (!canSubmit) return
    try {
      await release.trigger({ driver_id: driver.driver_id, reason: reason.trim() }).result
      toast.success('Bench released', {
        description: `${driver.driver_name || 'The driver'} can be allocated again.`,
      })
      setOpen(false)
      setReason('')
      onReleased()
    } catch (err) {
      toast.error('Not released', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setReason('')
        setOpen(next)
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-6 shrink-0 gap-1 px-1.5 text-[11px]">
          <Undo2 className="h-3 w-3" />
          Release
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Release from the bench</DialogTitle>
          <DialogDescription>
            {driver.driver_name || 'This driver'} becomes allocatable again. This does not seat him:
            allocate him to a car once the bench is released.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Benched {driver.benched_since ? `on ${formatFullDate(driver.benched_since)}` : ''}
            </p>
            <p className="mt-0.5 text-foreground">{driver.bench_reason || 'No reason recorded.'}</p>
            {driver.benched_by ? (
              <p className="mt-0.5 text-xs text-muted-foreground">By {driver.benched_by}</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="release-reason">Reason</Label>
            <Textarea
              id="release-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is the bench ending? He must be able to be told."
              rows={3}
            />
            {!reasonValid && reason.length > 0 && (
              <p className="text-xs text-destructive">A reason cannot be blank.</p>
            )}
          </div>

          {release.error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{release.error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={release.loading}>
            Cancel
          </Button>
          <Button onClick={handleRelease} disabled={!canSubmit} className="gap-1.5">
            {release.loading ? <Spinner className="h-4 w-4" /> : <Undo2 className="h-4 w-4" />}
            Release
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function BenchedDriversPanel({
  bench,
  canEdit,
  onChanged,
}: {
  bench: BenchDriver[]
  canEdit: boolean
  onChanged: () => void
}) {
  const benched = bench.filter((d) => d.benched)
  if (benched.length === 0) return null

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground">
        <UserMinus className="h-4 w-4 text-muted-foreground" />
        {benched.length === 1 ? '1 driver is benched' : `${benched.length} drivers are benched`}
      </h2>
      <p className="mb-2 text-xs text-muted-foreground">
        Held off the roster on purpose. A benched driver works no shifts while the bench stands, so
        release him as soon as he is due back.
      </p>
      <ul className="space-y-2">
        {benched.map((d) => (
          <li
            key={d.driver_id}
            className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-2.5"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-foreground">
                {d.driver_name || 'Unnamed driver'}
              </p>
              <p className="text-xs leading-snug text-muted-foreground">
                {d.bench_reason || 'No reason recorded.'}
              </p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {d.benched_since ? `Since ${formatFullDate(d.benched_since)}` : 'Date not recorded'}
                {d.benched_by ? `, by ${d.benched_by}` : ''}
              </p>
            </div>
            {canEdit ? <ReleaseBenchDialog driver={d} onReleased={onChanged} /> : null}
          </li>
        ))}
      </ul>
    </div>
  )
}
